import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  CheckCircle2,
  ChevronRight,
  Crown,
  Info,
  Loader2,
  Sparkles,
  TicketPercent,
  X,
} from "lucide-react";
import { collection, getDocs, onSnapshot, orderBy, query, where } from "firebase/firestore";
import SettingsLayout from "./SettingsLayout";
import { invalidateAuthCache } from "../../hooks/useAuthAccess";
import useSettingsProfile from "./useSettingsProfile";
import { auth, db } from "../../firebase";
import {
  createPremiumOrderWithCoupon,
  recordFailedPayment,
  validatePremiumCoupon,
  verifyPremiumPayment,
} from "../../services/api";
import { loadRazorpayScript, openRazorpayCheckout } from "../../utils/razorpayCheckout";
import {
  formatMembershipDate,
  isPremiumActive,
  parseMembershipDate,
} from "../../utils/membership";
import {
  formatDiscountLabel,
  normalizeCouponInput,
} from "../../utils/coupons";

/* =====================================================================
   Plans
===================================================================== */
const PLANS = [
  {
    id: "premium_6months",
    name: "6 Months Premium",
    duration: "6 Months",
    durationMonths: 6,
    originalPrice: 799900, // ₹7,999 in paise (display strikethrough)
    offerPrice: 499900,    // ₹4,999 in paise (actual charge)
    currency: "INR",
    badge: null,
    gstPercent: 0,
  },
  {
    id: "premium_annual",
    name: "1 Year Premium",
    duration: "1 Year",
    durationMonths: 12,
    originalPrice: 1199900, // ₹11,999 in paise (display strikethrough)
    offerPrice: 699900,     // ₹6,999 in paise (actual charge)
    currency: "INR",
    badge: "Most Popular",
    gstPercent: 0,
  },
];

function formatRupees(paise) {
  const amount = Math.round(paise / 100);
  return `₹${amount.toLocaleString("en-IN")}`;
}

/* =====================================================================
   Main
===================================================================== */
export default function MembershipSettings() {
  const navigate = useNavigate();
  const { user, profile, profileId, loading, error, setError } = useSettingsProfile();
  const [availablePlans, setAvailablePlans] = useState(PLANS);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansMessage, setPlansMessage] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(PLANS.find((plan) => plan.badge) || PLANS[0] || null);
  const [paying, setPaying] = useState(false);
  const [paymentStage, setPaymentStage] = useState("");
  const [successReceipt, setSuccessReceipt] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponFeedback, setCouponFeedback] = useState({ tone: "", message: "" });
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const premiumActive = isPremiumActive(profile);
  const expiresAt = parseMembershipDate(profile?.membershipExpiresAt);
  const isExpired =
    String(profile?.membership || "").toLowerCase() === "premium" &&
    expiresAt &&
    expiresAt.getTime() <= Date.now();
  const activePlans = useMemo(
    () => availablePlans.filter((plan) => plan && plan.active && plan.offerPrice > 0),
    [availablePlans]
  );
  const paymentsAvailable = activePlans.length > 0;
  const paymentsUnavailableMessage = useMemo(() => {
    const reason = plansMessage.trim();
    if (!reason) {
      return "Premium payments are currently unavailable. Please contact admin for membership assistance.";
    }
    return `Premium payments are currently unavailable: ${reason}`;
  }, [plansMessage]);

  useEffect(() => {
    let cancelled = false;

    const normalizeCatalogPlan = (catalogPlan, fallback) => {
      const finalAmount = Number(catalogPlan?.finalAmount ?? fallback.offerPrice) || 0;
      const baseAmount = Number(catalogPlan?.baseAmount ?? finalAmount) || finalAmount;
      const originalAmount =
        Number(catalogPlan?.originalAmount ?? catalogPlan?.originalPrice ?? fallback.originalPrice) ||
        Math.max(baseAmount, finalAmount);
      const durationMonths =
        Number(catalogPlan?.durationMonths ?? fallback.durationMonths) || fallback.durationMonths;

      return {
        ...fallback,
        id: catalogPlan?.id || fallback.id,
        name: catalogPlan?.name || fallback.name,
        duration: durationMonths === 12 ? "1 Year" : `${durationMonths} Months`,
        durationMonths,
        originalPrice: Math.max(originalAmount, finalAmount),
        offerPrice: finalAmount,
        currency: catalogPlan?.currency || fallback.currency,
        badge: catalogPlan?.badge || fallback.badge,
        gstPercent: Number(catalogPlan?.gstPercent ?? fallback.gstPercent) || 0,
        active: catalogPlan?.active !== false,
        disabledReason: catalogPlan?.disabledReason || "",
      };
    };

    const loadPlans = () => {
      setPlansLoading(true);
      setPlansMessage("");

      const planQuery = query(collection(db, "membershipPlans"), orderBy("sortOrder", "asc"));

      const unsubscribe = onSnapshot(
        planQuery,
        (snapshot) => {
          if (cancelled) return;

          const catalogPlans = PLANS.map((fallback) => {
            const catalogDoc = snapshot.docs.find((docSnap) => docSnap.id === fallback.id);
            return normalizeCatalogPlan(
              catalogDoc ? { id: catalogDoc.id, ...catalogDoc.data() } : null,
              fallback
            );
          });

          const disabledReason =
            catalogPlans.find((plan) => plan && !plan.active && plan.disabledReason)?.disabledReason ||
            "";

          setAvailablePlans(catalogPlans);
          setSelectedPlan((current) => {
            if (current && catalogPlans.some((plan) => plan.id === current.id && plan.active)) {
              return catalogPlans.find((plan) => plan.id === current.id) || null;
            }
            return (
              catalogPlans.find((plan) => plan.active && plan.badge) ||
              catalogPlans.find((plan) => plan.active) ||
              null
            );
          });

          setPlansMessage(
            catalogPlans.some((plan) => plan.active)
              ? ""
              : disabledReason ||
                  "Premium payments are currently unavailable. Please contact admin for membership assistance."
          );
          setPlansLoading(false);
        },
        () => {
          if (cancelled) return;
          setAvailablePlans(PLANS);
          setSelectedPlan((current) =>
            current && PLANS.some((plan) => plan.id === current.id) ? current : PLANS[0] || null
          );
          setPlansMessage("");
          setPlansLoading(false);
        }
      );

      return unsubscribe;
    };

    const unsubscribe = loadPlans();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  // Effective pricing — applies coupon discount on top of the plan offer price
  const pricing = useMemo(() => {
    if (!selectedPlan) {
      return {
        originalPrice: 0,
        offerPrice: 0,
        couponDiscount: 0,
        finalAmount: 0,
        totalSavings: 0,
      };
    }

    const offerPrice = selectedPlan.offerPrice;
    const originalPrice = selectedPlan.originalPrice;
    const cp = appliedCoupon?.pricing;
    if (cp && typeof cp.finalAmount === "number") {
      const couponDiscount = Math.max(0, (cp.originalFinalAmount ?? offerPrice) - cp.finalAmount);
      return {
        originalPrice,
        offerPrice,
        couponDiscount,
        finalAmount: cp.finalAmount,
        totalSavings: originalPrice - cp.finalAmount,
      };
    }
    return {
      originalPrice,
      offerPrice,
      couponDiscount: 0,
      finalAmount: offerPrice,
      totalSavings: originalPrice - offerPrice,
    };
  }, [selectedPlan, appliedCoupon]);

  // Load offers/coupons
  useEffect(() => {
    setOffersLoading(true);
    const loadOffers = async () => {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, "coupons"),
            where("active", "==", true),
            where("visible", "==", true)
          )
        );
        const now = Date.now();
        const nextOffers = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((coupon) => {
            const exp = parseMembershipDate(coupon.expiresAt);
            return !(exp && exp.getTime() <= now);
          });
        setOffers(nextOffers);
      } catch {
        setOffers([]);
      } finally {
        setOffersLoading(false);
      }
    };
    loadOffers();
  }, []);

  useEffect(() => {
    if (successReceipt) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [successReceipt]);

  const applyCoupon = async (incomingCode = couponInput) => {
    if (!user || !selectedPlan || !paymentsAvailable) return;
    const code = normalizeCouponInput(incomingCode);
    if (!code) {
      setCouponFeedback({ tone: "error", message: "Enter a coupon code." });
      setAppliedCoupon(null);
      return;
    }
    try {
      setValidatingCoupon(true);
      setCouponFeedback({ tone: "", message: "" });
      const idToken = await auth.currentUser?.getIdToken();
      const result = await validatePremiumCoupon(idToken, { couponCode: code, planId: selectedPlan.id });
      setAppliedCoupon(result);
      setCouponInput(code);
      setCouponFeedback({ tone: "success", message: result.message || `${code} applied!` });
    } catch (applyError) {
      setAppliedCoupon(null);
      setCouponFeedback({ tone: "error", message: applyError.message || "Invalid coupon." });
    } finally {
      setValidatingCoupon(false);
    }
  };

  const clearCoupon = () => {
    setCouponInput("");
    setAppliedCoupon(null);
    setCouponFeedback({ tone: "", message: "" });
  };

  const startCheckout = async () => {
    if (!paymentsAvailable || !selectedPlan) {
      setError(paymentsUnavailableMessage);
      return;
    }

    if (!user || !profileId) {
      setError("Complete your profile before upgrading.");
      return;
    }
    setPaying(true);
    setPaymentStage("preparing");
    setSuccessReceipt(null);
    setError("");
    try {
      const scriptReady = await loadRazorpayScript();
      if (!scriptReady) throw new Error("Could not load Razorpay. Check your connection.");
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error("Please log in again.");

      const order = await createPremiumOrderWithCoupon(idToken, {
        profileId,
        planId: selectedPlan.id,
        couponCode: appliedCoupon?.coupon?.code || normalizeCouponInput(couponInput),
      });

      setPaymentStage("");
      openRazorpayCheckout({
        keyId: order.keyId,
        orderId: order.orderId,
        amount: order.amount,
        currency: order.currency,
        planName: order.plan?.name || selectedPlan.name,
        userName: profile?.fullName,
        userEmail: user.email,
        userPhone: profile?.mobileNumber || profile?.phone,
        onSuccess: async (response) => {
          setPaymentStage("verifying");
          try {
            const result = await verifyPremiumPayment(idToken, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              profileId: order.profileId,
            });
            invalidateAuthCache();
            try {
              const savedAuth = JSON.parse(localStorage.getItem("karankiAuth") || "null");
              if (savedAuth) {
                localStorage.setItem(
                  "karankiAuth",
                  JSON.stringify({
                    ...savedAuth,
                    membership: "premium",
                    membershipExpiresAt: result.receipt?.validUntil || null,
                  })
                );
              }
            } catch {
              // ignore local session update failures
            }
            setSuccessReceipt(result.receipt || {});
            setError("");
          } catch (verifyError) {
            setError(
              verifyError.message ||
                "Your payment was received but we couldn't activate premium automatically. Please contact support with your payment ID."
            );
          } finally {
            setPaying(false);
            setPaymentStage("");
          }
        },
        onFailure: (failure) => {
          setPaying(false);
          setPaymentStage("");
          setError(
            failure.description
              ? `Payment failed: ${failure.description}`
              : "Payment failed. No money was deducted. Please try again."
          );
          // Log the failed attempt (best-effort)
          recordFailedPayment(idToken, {
            orderId: failure.orderId || order.orderId,
            paymentId: failure.paymentId || "",
            profileId: order.profileId,
            planId: selectedPlan.id,
            errorCode: failure.code,
            errorDescription: failure.description,
            errorReason: failure.reason,
            amount: order.amount,
          });
        },
        onDismiss: () => { setPaying(false); setPaymentStage(""); },
      });
    } catch (checkoutError) {
      setError(checkoutError.message || "Could not start checkout.");
      setPaying(false);
      setPaymentStage("");
    }
  };

  return (
    <SettingsLayout
      title="Premium Membership"
      description="Unlock the full Karanki Matrimony experience."
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-brand-600" size={32} />
        </div>
      ) : (
        <>
          {/* Success receipt */}
          {successReceipt && (
            <div className="sr-only" role="status">Payment successful. Premium membership activated.</div>
          )}

          {/* Current status */}
          {!successReceipt && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`overflow-hidden rounded-3xl border p-4 shadow-sm sm:p-5 ${
                premiumActive
                  ? "border-brand-100 bg-brand-50/60"
                  : isExpired
                    ? "border-amber-200 bg-amber-50/60"
                    : "border-sand-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-3.5">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                  premiumActive ? "bg-brand-100 text-brand-700" : isExpired ? "bg-amber-100 text-amber-700" : "bg-sand-100 text-ink-500"
                }`}>
                  <Crown size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-ink-500">Current plan</p>
                  <h2 className="mt-0.5 font-display text-[18px] font-bold leading-6 text-ink-800">
                    {premiumActive ? "Premium" : isExpired ? "Expired" : "Free"}
                  </h2>
                  {premiumActive && expiresAt && (
                    <p className="mt-0.5 text-[11.5px] text-ink-500">Valid until {formatMembershipDate(expiresAt)}</p>
                  )}
                  {isExpired && expiresAt && (
                    <p className="mt-0.5 text-[11.5px] text-amber-800">Expired on {formatMembershipDate(expiresAt)}</p>
                  )}
                </div>
                {premiumActive && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">Active</span>}
              </div>
            </motion.div>
          )}

          {/* Plans */}
          {!successReceipt && !premiumActive && plansLoading && (
            <div className="mt-6 rounded-3xl border border-sand-200 bg-white p-4 text-[12.5px] font-semibold text-ink-500">
              Syncing live plan status...
            </div>
          )}

          {!successReceipt && !premiumActive && !paymentsAvailable && (
            <section className="mt-4 rounded-3xl border border-amber-200 bg-amber-50/60 p-4 sm:mt-5 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <Info size={17} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-[14px] font-semibold text-ink-800">Payments unavailable</h2>
                  <p className="mt-1 text-[12px] leading-5 text-amber-800">
                    {paymentsUnavailableMessage}
                  </p>
                </div>
              </div>
            </section>
          )}

          {!successReceipt && !premiumActive && paymentsAvailable && (
            <section className="mt-6">
              <div className="mb-3">
                <h2 className="font-display text-lg font-bold text-ink-800">Choose your plan</h2>
                <p className="mt-0.5 text-[12.5px] text-ink-500">Select the membership that suits you</p>
              </div>

              <div className="overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-sm">
                {availablePlans.map((plan, index) => {
                  const active = selectedPlan?.id === plan.id;
                  const disabled = !plan.active;
                  const save = plan.originalPrice - plan.offerPrice;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      disabled={disabled}
                      aria-pressed={active}
                      onClick={() => {
                        if (disabled) return;
                        setSelectedPlan(plan);
                        clearCoupon();
                      }}
                      className={`relative flex w-full items-center gap-3 px-3.5 py-4 text-left transition sm:px-5 ${
                        index > 0 ? "border-t border-sand-100" : ""
                      } ${
                        disabled
                          ? "cursor-not-allowed bg-sand-50/70 opacity-70"
                          : active
                            ? "bg-brand-50/60"
                            : "bg-white hover:bg-sand-50/70"
                      }`}
                    >
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                        active && !disabled ? "border-brand-600 bg-brand-600 text-white" : "border-sand-300 bg-white"
                      }`}>
                        {active && !disabled && <Check size={12} strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[12px] font-semibold uppercase tracking-wide text-ink-600">{plan.duration}</span>
                          {plan.badge && (
                            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand-700">
                              {plan.badge}
                            </span>
                          )}
                        </span>
                        {disabled ? (
                          <span className="mt-1 block text-[11px] font-medium text-amber-800">
                            Unavailable{plan.disabledReason ? `: ${plan.disabledReason}` : ""}
                          </span>
                        ) : (
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
                            <Sparkles size={11} /> Save {formatRupees(save)}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block font-display text-[20px] font-bold leading-6 text-ink-800">{formatRupees(plan.offerPrice)}</span>
                        <span className="mt-0.5 block text-[11px] text-ink-400 line-through">{formatRupees(plan.originalPrice)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Coupons — public offers + manual input */}
          {!successReceipt && !premiumActive && paymentsAvailable && (
            <section className="mt-6">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-ink-800">Have a coupon?</h2>
                {appliedCoupon && (
                  <button type="button" onClick={clearCoupon} className="text-[12px] font-semibold text-rose-500 hover:underline">
                    Remove
                  </button>
                )}
              </div>

              {/* Manual input */}
              {!appliedCoupon && (
                <div className="mt-3 flex gap-2">
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(normalizeCouponInput(e.target.value))}
                    placeholder="Enter coupon code"
                    className="input flex-1 uppercase tracking-wider text-[14px]"
                  />
                  <button
                    type="button"
                    onClick={() => applyCoupon()}
                    disabled={validatingCoupon || !couponInput}
                    className="btn-primary shrink-0 px-5 disabled:opacity-50"
                  >
                    {validatingCoupon ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
                  </button>
                </div>
              )}

              {/* Applied coupon display */}
              {appliedCoupon && (
                <div className="mt-3 flex items-center gap-3 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                    <TicketPercent size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] font-bold text-emerald-800">{appliedCoupon.coupon?.code}</p>
                    <p className="text-[11.5px] text-emerald-600">{couponFeedback.message || "Coupon applied"}</p>
                  </div>
                  <CheckCircle2 size={20} className="text-emerald-600" />
                </div>
              )}

              {/* Error */}
              {couponFeedback.message && couponFeedback.tone === "error" && (
                <p className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-[12.5px] font-semibold text-rose-700">
                  {couponFeedback.message}
                </p>
              )}

              {/* Public offers */}
              {!offersLoading && offers.length > 0 && !appliedCoupon && (
                <div className="mt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Or choose an offer</p>
                  <div className="mt-2 space-y-2">
                    {offers.map((offer) => (
                      <button
                        key={offer.id}
                        type="button"
                        disabled={validatingCoupon}
                        onClick={() => { setCouponInput(offer.code); applyCoupon(offer.code); }}
                        className="flex w-full items-center gap-3 rounded-2xl border border-sand-200 bg-white p-3 text-left transition hover:border-brand-300"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                          <TicketPercent size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-ink-800">{formatDiscountLabel(offer)}</p>
                          <p className="truncate text-[11px] text-ink-400">{offer.description || offer.code}</p>
                        </div>
                        <span className="rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-bold text-brand-600">
                          Apply
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Benefits — Free vs Premium comparison */}
          <section className="mt-6">
            <h2 className="font-display text-lg font-bold text-ink-800">Free vs Premium</h2>
            <p className="mt-0.5 text-[12.5px] text-ink-400">See what you unlock with Premium</p>

            <div className="mt-3 overflow-hidden rounded-2xl border border-sand-200 sm:mt-4 sm:rounded-3xl">
            <div className="grid grid-cols-[minmax(0,1fr)_52px_68px] items-center border-b border-sand-200 bg-sand-50/70 px-3 py-2.5 text-[9px] font-semibold uppercase tracking-wide text-ink-500 sm:grid-cols-[1fr_70px_80px] sm:px-4 sm:py-3 sm:text-[11px] sm:tracking-wider">
                <span>Feature</span>
                <span className="text-center">Free</span>
              <span className="text-center text-brand-700">Premium</span>
              </div>
              {[
                { feature: "Browse matches", free: true, premium: true },
                { feature: "View profiles", free: true, premium: true },
                { feature: "Send interests", free: "5 only", premium: true },
                { feature: "Receive interests", free: true, premium: true },
                { feature: "View contact number", free: false, premium: true },
                { feature: "See who viewed you", free: "Count only", premium: true },
                { feature: "View all photos", free: "1st only", premium: true },
                { feature: "Advanced filters", free: false, premium: true },
                { feature: "Priority listing", free: false, premium: true },
                { feature: "Premium badge", free: false, premium: true },
              ].map(({ feature, free, premium: prem }, idx) => (
                <div
                  key={feature}
                  className={`grid grid-cols-[minmax(0,1fr)_52px_68px] items-center border-b border-sand-100 px-3 py-3 last:border-b-0 sm:grid-cols-[1fr_70px_80px] sm:px-4 ${
                    idx % 2 === 0 ? "bg-white" : "bg-sand-50/40"
                  }`}
                >
                  <span className="pr-1 text-[11.5px] font-medium leading-4 text-ink-700 sm:text-[13px]">{feature}</span>
                  <span className="flex justify-center">
                    {free === true ? (
                      <Check size={16} className="text-emerald-500" />
                    ) : free === false ? (
                      <X size={16} className="text-rose-400" />
                    ) : (
                      <span className="text-center text-[9px] font-medium leading-3 text-amber-700 sm:text-[10.5px]">{free}</span>
                    )}
                  </span>
                  <span className="flex justify-center">
                    {prem === true ? (
                      <Check size={16} className="text-emerald-500" />
                    ) : (
                      <X size={16} className="text-rose-400" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Pay button — desktop inline */}
          {!successReceipt && !premiumActive && paymentsAvailable && (
            <div className="mt-6 hidden lg:block">
              <div className="card overflow-hidden">
                <div className="space-y-2 bg-sand-50 px-5 py-4">
                  <div className="flex items-center justify-between text-[13px] text-ink-500">
                    <span>Plan price</span>
                    <span className="line-through">{formatRupees(pricing.originalPrice)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px] text-ink-600">
                    <span>Offer price</span>
                    <span className="font-semibold">{formatRupees(pricing.offerPrice)}</span>
                  </div>
                  {pricing.couponDiscount > 0 && (
                    <div className="flex items-center justify-between text-[13px] text-emerald-600">
                      <span>Coupon discount</span>
                      <span className="font-semibold">−{formatRupees(pricing.couponDiscount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-dashed border-sand-300 pt-2.5">
                    <span className="text-[14px] font-bold text-ink-800">Total payable</span>
                    <div className="text-right">
                      <span className="font-display text-xl font-bold text-brand-700">{formatRupees(pricing.finalAmount)}</span>
                      {pricing.totalSavings > 0 && (
                        <p className="text-[11px] font-semibold text-emerald-600">You save {formatRupees(pricing.totalSavings)}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <button
                    type="button"
                    onClick={startCheckout}
                    disabled={paying}
                    className="btn-primary w-full py-4 text-[15px]"
                  >
                    {paying ? (
                      <><Loader2 size={18} className="animate-spin" /> Processing...</>
                    ) : isExpired ? (
                      <><Crown size={16} /> Renew Premium</>
                    ) : (
                      <><Crown size={16} /> Pay {formatRupees(pricing.finalAmount)}</>
                    )}
                  </button>
                  <p className="mt-2.5 text-center text-[11px] text-ink-400">
                    Secure payment via Razorpay. No auto-renewal.
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-700"
            >
              {error}
            </motion.div>
          )}

          {/* Spacer for sticky bar on mobile */}
          {!successReceipt && !premiumActive && paymentsAvailable && <div className="h-32 lg:hidden" />}
        </>
      )}

      {/* Sticky mobile pay bar */}
      {!loading && !successReceipt && !premiumActive && paymentsAvailable && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-200 bg-white/95 px-4 pt-3 shadow-[0_-8px_24px_rgba(37,20,24,0.12)] backdrop-blur-xl lg:hidden"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <div className="mx-auto max-w-md">
            {/* View details toggle */}
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              className="mb-2 flex w-full items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">Total payable</span>
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-brand-700">
                  Details <ChevronRight size={11} className="rotate-90" />
                </span>
              </div>
              {pricing.totalSavings > 0 && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9.5px] font-semibold text-emerald-700">
                  Save {formatRupees(pricing.totalSavings)}
                </span>
              )}
            </button>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-1.5">
                <span className="font-display text-[21px] font-bold text-ink-800">{formatRupees(pricing.finalAmount)}</span>
                <span className="text-[11px] text-ink-400 line-through">{formatRupees(pricing.originalPrice)}</span>
              </div>
              <button
                type="button"
                onClick={startCheckout}
                disabled={paying}
                className="btn-primary shrink-0 rounded-2xl px-5 py-3 text-[13px]"
              >
                {paying ? <Loader2 size={16} className="animate-spin" /> : <Crown size={15} />}
                {paying ? "Wait..." : "Pay Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile price breakdown sheet */}
      <AnimatePresence>
        {paymentStage && (
          <motion.div
            key="payment-progress"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-900/45 px-5 backdrop-blur-sm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="payment-progress-title"
            aria-describedby="payment-progress-description"
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              className="w-full max-w-sm rounded-3xl bg-white px-6 py-7 text-center shadow-elevated"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                <Loader2 size={25} className="animate-spin" />
              </div>
              <h2 id="payment-progress-title" className="mt-4 font-display text-lg font-bold text-ink-800">
                {paymentStage === "verifying" ? "Confirming your payment" : "Please wait"}
              </h2>
              <p id="payment-progress-description" className="mx-auto mt-2 max-w-[260px] text-[13px] leading-5 text-ink-500">
                {paymentStage === "verifying"
                  ? "Your payment was submitted. We’re securely verifying it and activating Premium. Please don’t close this page."
                  : "We’re preparing your secure checkout. This will only take a moment."}
              </p>
            </motion.div>
          </motion.div>
        )}
        {successReceipt && (
          <motion.div
            key="payment-success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-900/50 px-5 py-6 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-success-title"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className="relative w-full max-w-sm overflow-hidden rounded-[28px] bg-white text-center shadow-[0_24px_80px_rgba(24,14,18,0.28)]"
            >
              <div className="relative overflow-hidden px-6 pb-5 pt-8">
                <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-64 -translate-x-1/2 rounded-full bg-emerald-50/70 blur-3xl" />
                <div className="relative mx-auto flex h-[76px] w-[76px] items-center justify-center">
                  <motion.span
                    aria-hidden="true"
                    initial={{ scale: 0.65, opacity: 0 }}
                    animate={{ scale: [0.65, 1.12, 1], opacity: [0, 0.55, 0] }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="absolute inset-0 rounded-full border-2 border-emerald-300"
                  />
                  <motion.span
                    aria-hidden="true"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.12, type: "spring", stiffness: 260, damping: 14 }}
                    className="absolute inset-1 rounded-full bg-emerald-50"
                  />
                  <motion.svg
                    aria-hidden="true"
                    viewBox="0 0 48 48"
                    className="relative h-12 w-12 text-emerald-600"
                    initial={{ scale: 0.7 }}
                    animate={{ scale: [0.7, 1.16, 1] }}
                    transition={{ delay: 0.16, duration: 0.55, ease: "easeOut" }}
                  >
                    <motion.circle
                      cx="24"
                      cy="24"
                      r="18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.25, duration: 0.55, ease: "easeOut" }}
                    />
                    <motion.path
                      d="m16 24 5 5 11-12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.58, duration: 0.35, ease: "easeOut" }}
                    />
                  </motion.svg>
                  {[
                    ["left-0 top-4", "-10px", "-14px", "#10b981"],
                    ["right-0 top-5", "12px", "-12px", "#f59e0b"],
                    ["left-2 bottom-3", "-13px", "12px", "#fb7185"],
                    ["right-2 bottom-2", "13px", "14px", "#34d399"],
                    ["left-1/2 -top-1", "0px", "-16px", "#fbbf24"],
                  ].map(([position, x, y, color], index) => (
                    <motion.span
                      key={position}
                      aria-hidden="true"
                      initial={{ opacity: 0, x: 0, y: 0, rotate: 0, scale: 0.4 }}
                      animate={{ opacity: [0, 1, 0], x, y, rotate: index % 2 ? 100 : -100, scale: [0.4, 1, 0.7] }}
                      transition={{ delay: 0.22 + index * 0.045, duration: 0.85, ease: "easeOut" }}
                      className={`absolute ${position} h-2 w-1.5 rounded-sm`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.42 }}
                  className="relative mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700"
                >
                  Payment successful
                </motion.p>
                <motion.h2
                  id="payment-success-title"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="relative mt-1 font-display text-2xl font-bold text-ink-800"
                >
                  Premium is active!
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.58 }}
                  className="relative mt-2 text-[13px] leading-5 text-ink-500"
                >
                  Your membership has been upgraded. Enjoy connecting with more matches.
                </motion.p>
                {successReceipt.validUntil && (
                  <motion.p
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.66 }}
                    className="relative mt-4 rounded-2xl bg-sand-50 px-4 py-3 text-[12px] text-ink-600"
                  >
                    Premium access until{" "}
                    <span className="font-semibold text-ink-800">
                      {formatMembershipDate(parseMembershipDate(successReceipt.validUntil))}
                    </span>
                  </motion.p>
                )}
              </div>
              <div className="border-t border-sand-100 p-4">
                <button
                  type="button"
                  onClick={() => navigate("/matches")}
                  className="btn-primary w-full rounded-2xl py-3.5"
                >
                  Explore matches <ChevronRight size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showDetails && paymentsAvailable && selectedPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetails(false)}
            className="fixed inset-0 z-[85] bg-ink-900/50 backdrop-blur-sm lg:hidden"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute inset-x-0 bottom-0 rounded-t-[28px] bg-white px-5 pb-7 pt-3"
            >
              <div className="mx-auto h-1.5 w-12 rounded-full bg-sand-200" />
              <div className="mt-4 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-ink-800">Price details</h3>
                <button type="button" onClick={() => setShowDetails(false)} className="btn-icon h-9 w-9">
                  <X size={16} />
                </button>
              </div>

              <p className="mt-1 text-[12.5px] text-ink-400">{selectedPlan.name}</p>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between text-[13.5px] text-ink-500">
                  <span>Plan price</span>
                  <span className="line-through">{formatRupees(pricing.originalPrice)}</span>
                </div>
                <div className="flex items-center justify-between text-[13.5px] text-ink-700">
                  <span>Offer price</span>
                  <span className="font-semibold">{formatRupees(pricing.offerPrice)}</span>
                </div>
                {pricing.couponDiscount > 0 && (
                  <div className="flex items-center justify-between text-[13.5px] text-emerald-600">
                    <span className="inline-flex items-center gap-1">
                      <TicketPercent size={13} />
                      Coupon {appliedCoupon?.coupon?.code}
                    </span>
                    <span className="font-semibold">−{formatRupees(pricing.couponDiscount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-2xl bg-emerald-50 px-4 py-2.5">
                  <span className="text-[13px] font-semibold text-emerald-700">You save</span>
                  <span className="font-display text-[15px] font-bold text-emerald-700">{formatRupees(pricing.totalSavings)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-dashed border-sand-300 pt-3">
                  <span className="text-[15px] font-bold text-ink-800">Total payable</span>
                  <span className="font-display text-2xl font-bold text-brand-700">{formatRupees(pricing.finalAmount)}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => { setShowDetails(false); startCheckout(); }}
                disabled={paying}
                className="btn-primary mt-5 w-full py-4 text-[15px]"
              >
                {paying ? <><Loader2 size={18} className="animate-spin" /> Processing...</> : <><Crown size={16} /> Pay {formatRupees(pricing.finalAmount)}</>}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </SettingsLayout>
  );
}
